// ============================================
// APP WEB - Main Entry Point
// ============================================

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, defaultShouldDehydrateQuery } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { RouterProvider } from 'react-router-dom';
import { useHeartbeat } from '@almacenes/core';
import { router } from './router';
import './index.css';

// Configuración de React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutos
      gcTime: 10 * 60 * 1000, // 10 minutos - tiempo que se mantiene en caché
    },
    mutations: {
      retry: 0,
    },
  },
});

// Persister para localStorage
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'almacenes-digitales-cache',
});

// Componente wrapper para hooks globales
const AppWrapper = () => {
  useHeartbeat(); // Ejecutar heartbeat una vez al día
  
  return (
    <>
      <RouterProvider router={router} />
      <ReactQueryDevtools initialIsOpen={false} />
    </>
  );
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24, // 24 horas
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            // Excluye queries con gcTime:0 (ej. changelog/activas) -- ese valor ya
            // declara "nunca cachear"; persistirlas en localStorage las revive como
            // "frescas" (staleTime:Infinity) en cada reload y nunca vuelven a pedirse.
            if (!defaultShouldDehydrateQuery(query) || query.gcTime === 0) return false;

            // Excluye las listas de boletas: con staleTime:Infinity para rangos
            // cerrados, su gcTime sube a 24h y quedarían candidatas a persistirse.
            // createSyncStoragePersister serializa TODO el caché de forma síncrona
            // en cada escritura; en un almacén de alto flujo un mes de boletas
            // puede acercarse a la cuota de localStorage (~5MB) y provocar
            // QuotaExceededError o jank. Este caché vive solo en memoria/sesión.
            const [dominio, tipo] = query.queryKey as string[];
            if (dominio === 'boletas' && tipo === 'filtros') return false;

            // Excluye el estado "en vivo" de Caja 360 (turno actual, detalle,
            // turnos abiertos, movimientos): estas queries ya se refrescan solas
            // cada 15-30s mientras la pestaña está enfocada, así que persistirlas
            // 24h en localStorage no aporta nada y sí puede hacer que, tras un
            // reload o un reset de datos (ej. entorno local), la UI muestre un
            // turnoId que el backend ya no reconoce -> 404 al cerrar caja.
            if (dominio === 'caja' && typeof tipo === 'string' &&
                (tipo === 'turno-actual' || tipo === 'turno' || tipo.startsWith('turnos') || tipo.startsWith('movimientos'))) {
              return false;
            }

            // Excluye reportes: la pantalla ahora exige un "Buscar" explícito (sin
            // autobúsqueda), así que en un reload no se dispara nada solo y persistir
            // 24h no aporta -- solo consume cuota de localStorage con payloads grandes
            // (detalle por producto, reporte diario).
            if (dominio === 'reportes') return false;

            return true;
          },
        },
      }}
    >
      <AppWrapper />
    </PersistQueryClientProvider>
  </React.StrictMode>
);
